package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminSettingsMailUpsert
import org.gymly.theme.AdminColors
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.rememberPasswordVisible
import org.gymly.util.userMessage

@Composable
internal fun SettingsMailPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var configId by remember { mutableStateOf<String?>(null) }

    var host by remember { mutableStateOf("") }
    var port by remember { mutableStateOf("587") }
    var username by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var passwordConfigured by remember { mutableStateOf(false) }
    var fromAddress by remember { mutableStateOf("") }
    var useTls by remember { mutableStateOf(true) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            success = null
            try {
                val mail = AdminRepo.api.getSettingsMail()
                configId = mail.id
                host = mail.host
                port = mail.port.toString()
                username = mail.username.orEmpty()
                password = ""
                passwordConfigured = mail.passwordConfigured
                fromAddress = mail.fromAddress.orEmpty()
                useTls = mail.useTls
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar la configuración de correo.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    fun buildUpsert(): AdminSettingsMailUpsert? {
        val trimmedHost = host.trim()
        if (trimmedHost.isEmpty()) {
            error = "El host SMTP es obligatorio."
            return null
        }
        val parsedPort = port.trim().toIntOrNull()
        if (parsedPort == null || parsedPort !in 1..65535) {
            error = "Puerto inválido (1-65535)."
            return null
        }
        return AdminSettingsMailUpsert(
            host = trimmedHost,
            port = parsedPort,
            username = username.trim().ifBlank { null },
            fromAddress = fromAddress.trim().ifBlank { null },
            password = password.trim().ifBlank { null },
            useTls = useTls,
        )
    }

    VerticalScrollColumn(
        Modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(if (compact) 16.dp else 24.dp),
    ) {
        Text("Correo (SMTP)", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
        Spacer(Modifier.height(6.dp))
        Text(
            "Usado para recibos, confirmaciones de reserva y recuperación de contraseña.",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
        Spacer(Modifier.height(18.dp))

        when {
            loading -> Box(Modifier.fillMaxWidth().padding(40.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            else -> {
                error?.let {
                    Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 10.dp))
                }
                success?.let {
                    Text(it, color = AdminColors.Success, fontSize = 13.sp, modifier = Modifier.padding(bottom = 10.dp))
                }

                if (compact) {
                    MailField("Host SMTP", host, { host = it }, "smtp.gymly.demo")
                    Spacer(Modifier.height(12.dp))
                    MailField("Puerto", port, { port = it.filter { c -> c.isDigit() }.take(5) }, "587")
                    Spacer(Modifier.height(12.dp))
                    MailField("Usuario", username, { username = it }, "no-reply@gymly.demo")
                    Spacer(Modifier.height(12.dp))
                    MailField(
                        "Contraseña SMTP",
                        password,
                        { password = it },
                        if (passwordConfigured) "(guardada — escribe para cambiar)" else "opcional",
                        secret = true,
                    )
                    Spacer(Modifier.height(12.dp))
                    MailField("Dirección de origen", fromAddress, { fromAddress = it }, "Gymly <no-reply@gymly.demo>")
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        Column(Modifier.weight(1f)) {
                            MailField("Host SMTP", host, { host = it }, "smtp.gymly.demo")
                        }
                        Column(Modifier.weight(1f)) {
                            MailField("Puerto", port, { port = it.filter { c -> c.isDigit() }.take(5) }, "587")
                        }
                    }
                    Spacer(Modifier.height(14.dp))
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        Column(Modifier.weight(1f)) {
                            MailField("Usuario", username, { username = it }, "no-reply@gymly.demo")
                        }
                        Column(Modifier.weight(1f)) {
                            MailField(
                                "Contraseña SMTP",
                                password,
                                { password = it },
                                if (passwordConfigured) "(guardada — escribe para cambiar)" else "opcional",
                                secret = true,
                            )
                        }
                    }
                    Spacer(Modifier.height(14.dp))
                    MailField(
                        "Dirección de origen",
                        fromAddress,
                        { fromAddress = it },
                        "Gymly <no-reply@gymly.demo>",
                    )
                }

                Spacer(Modifier.height(14.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(
                        checked = useTls,
                        onCheckedChange = { useTls = it },
                        enabled = !saving,
                        colors = CheckboxDefaults.colors(
                            checkedColor = AdminColors.Primary,
                            uncheckedColor = AdminColors.Outline,
                        ),
                    )
                    Text("Usar TLS", color = AdminColors.Text, fontSize = 14.sp)
                }

                Spacer(Modifier.height(18.dp))
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Button(
                            onClick = {
                                val upsert = buildUpsert() ?: return@Button
                                scope.launch {
                                    saving = true
                                    error = null
                                    success = null
                                    try {
                                        val saved = if (configId.isNullOrBlank()) {
                                            runCatching { AdminRepo.api.createSettingsMail(upsert) }
                                                .getOrElse { AdminRepo.api.upsertSettingsMail(upsert) }
                                        } else {
                                            AdminRepo.api.upsertSettingsMail(upsert)
                                        }
                                        configId = saved.id
                                        host = saved.host
                                        port = saved.port.toString()
                                        username = saved.username.orEmpty()
                                        password = ""
                                        passwordConfigured = saved.passwordConfigured
                                        fromAddress = saved.fromAddress.orEmpty()
                                        useTls = saved.useTls
                                        success = "Configuración de correo guardada."
                                    } catch (e: Exception) {
                                        error = e.userMessage("No se pudo guardar.")
                                    } finally {
                                        saving = false
                                    }
                                }
                            },
                            enabled = !saving,
                            modifier = Modifier.fillMaxWidth().height(44.dp),
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                        }
                        OutlinedButton(
                            onClick = {
                                scope.launch {
                                    saving = true
                                    error = null
                                    success = null
                                    try {
                                        AdminRepo.api.deleteSettingsMail(configId)
                                        configId = null
                                        host = ""
                                        port = "587"
                                        username = ""
                                        fromAddress = ""
                                        useTls = true
                                        success = "Configuración de correo eliminada."
                                    } catch (e: Exception) {
                                        error = e.userMessage("No se pudo eliminar.")
                                    } finally {
                                        saving = false
                                    }
                                }
                            },
                            enabled = !saving && !configId.isNullOrBlank(),
                            modifier = Modifier.fillMaxWidth().height(44.dp),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text("Eliminar", color = AdminColors.Error)
                        }
                    }
                } else {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Button(
                            onClick = {
                                val upsert = buildUpsert() ?: return@Button
                                scope.launch {
                                    saving = true
                                    error = null
                                    success = null
                                    try {
                                        val saved = AdminRepo.api.upsertSettingsMail(upsert)
                                        configId = saved.id
                                        host = saved.host
                                        port = saved.port.toString()
                                        username = saved.username.orEmpty()
                                        password = ""
                                        passwordConfigured = saved.passwordConfigured
                                        fromAddress = saved.fromAddress.orEmpty()
                                        useTls = saved.useTls
                                        success = "Configuración de correo guardada."
                                    } catch (e: Exception) {
                                        error = e.userMessage("No se pudo guardar.")
                                    } finally {
                                        saving = false
                                    }
                                }
                            },
                            enabled = !saving,
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                        }
                        OutlinedButton(
                            onClick = {
                                scope.launch {
                                    saving = true
                                    error = null
                                    success = null
                                    try {
                                        AdminRepo.api.deleteSettingsMail(configId)
                                        configId = null
                                        host = ""
                                        port = "587"
                                        username = ""
                                        fromAddress = ""
                                        useTls = true
                                        success = "Configuración de correo eliminada."
                                    } catch (e: Exception) {
                                        error = e.userMessage("No se pudo eliminar.")
                                    } finally {
                                        saving = false
                                    }
                                }
                            },
                            enabled = !saving && !configId.isNullOrBlank(),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text("Eliminar", color = AdminColors.Error)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MailField(
    label: String,
    value: String,
    onChange: (String) -> Unit,
    placeholder: String,
    secret: Boolean = false,
) {
    Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    Spacer(Modifier.height(4.dp))
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth().widthIn(max = 640.dp),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        visualTransformation = if (secret && !visible) PasswordVisualTransformation() else VisualTransformation.None,
        trailingIcon = if (secret) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
